'use client';

/* ──────────────────────────────────────────────
 *  useStepTransitionFocus — scroll + focus au changement d'étape,
 *  partagé par les tunnels réservation, carte cadeau et course.
 *  Dans un embed au milieu d'une page longue, cliquer « Continuer » en bas du
 *  widget rend l'étape suivante sans bouger le viewport : on peut se retrouver
 *  face au pied de page hôte. Règles :
 *   - on ne scrolle QUE si le haut du widget est caché : sorti par le haut de
 *     l'écran, ou sous le menu collant du site (jamais de scroll si le haut du
 *     widget est déjà visible) ;
 *   - hauteur du menu collant : `scroll-margin-top: var(--bt-scroll-offset)` posé
 *     en CSS sur `.bt-root`, la variable venant de l'hôte. `scrollIntoView` s'arrête
 *     sous le menu, et le test de visibilité lit la même valeur ;
 *   - focus déplacé sur le titre d'étape pour l'annonce lecteur d'écran ;
 *   - `prefers-reduced-motion` respecté.
 * ────────────────────────────────────────────── */

import { type RefObject, useEffect, useRef } from 'react';

/**
 * @param step étape affichée : le scroll et le focus suivent chacun de ses changements.
 * @param skip transitions à ignorer (chargement initial, erreur) ; l'étape est mémorisée quand même.
 */
export function useStepTransitionFocus(
  rootRef: RefObject<HTMLDivElement | null>,
  step: string,
  skip?: (previous: string, next: string) => boolean,
): void {
  const prevStepRef = useRef<string>(step);
  const skipRef = useRef(skip);
  skipRef.current = skip;

  useEffect((): void => {
    const prev = prevStepRef.current;
    if (prev === step) return;
    prevStepRef.current = step;
    if (skipRef.current?.(prev, step)) return;

    const root = rootRef.current;
    if (!root) return;

    const stickyOffset = Number.parseFloat(window.getComputedStyle(root).scrollMarginTop) || 0;
    if (root.getBoundingClientRect().top < stickyOffset) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      root.scrollIntoView({ block: 'start', behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }

    const title = root.querySelector('.bt-step-header__title');
    if (title instanceof HTMLElement) {
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
    }
  }, [step, rootRef]);
}
